import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  fetchArtistMetrics, 
  fetchArtistCampaigns, 
  fetchRecommendedInfluencers,
  fetchArtistFinancialSummary 
} from '../../services/dashboardService';
import Header from '../../components/ui/Header';
import MobileNav from '../../components/ui/MobileNav';
import QuickActions from '../../components/ui/QuickActions';

import Button from '../../components/ui/Button';
import MetricsCard from './components/MetricsCard';
import CampaignCard from './components/CampaignCard';
import RecommendedInfluencer from './components/RecommendedInfluencer';
import RecentMessage from './components/RecentMessage';
import FinancialSummary from './components/FinancialSummary';
import QuickActionCard from './components/QuickActionCard';
import Icon from '../../components/AppIcon';

const ArtistDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [recommendedInfluencers, setRecommendedInfluencers] = useState([]);
  const [financialData, setFinancialData] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all dashboard data in parallel
      const [metricsData, campaignsData, influencersData, financialSummary] = await Promise.all([
        fetchArtistMetrics(user?.id),
        fetchArtistCampaigns(user?.id),
        fetchRecommendedInfluencers(4),
        fetchArtistFinancialSummary(user?.id)
      ]);

      // Set metrics
      if (metricsData) {
        setMetrics([
          {
            icon: 'Users',
            label: 'Toplam Erişim',
            value: metricsData?.totalReach,
            change: '+12,5%',
            changeType: 'positive',
            color: 'primary'
          },
          {
            icon: 'Heart',
            label: 'Etkileşim Oranı',
            value: `%${metricsData?.engagementRate}`,
            change: '+2,1%',
            changeType: 'positive',
            color: 'secondary'
          },
          {
            icon: 'TrendingUp',
            label: 'Aktif Kampanyalar',
            value: metricsData?.activeCampaigns,
            change: '+3',
            changeType: 'positive',
            color: 'accent'
          },
          {
            icon: 'DollarSign',
            label: 'Toplam Gelir',
            value: metricsData?.totalRevenue,
            change: '+18,3%',
            changeType: 'positive',
            color: 'success'
          }
        ]);
      }

      setCampaigns(campaignsData);
      setRecommendedInfluencers(influencersData);
      setFinancialData(financialSummary);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaignsData = async () => {
    try {
      const campaignsData = await fetchArtistCampaigns(user?.id);
      setCampaigns(campaignsData);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  };

  const loadMetricsData = async () => {
    try {
      const metricsData = await fetchArtistMetrics(user?.id);
      if (metricsData) {
        setMetrics([
          {
            icon: 'Users',
            label: 'Toplam Erişim',
            value: metricsData?.totalReach,
            change: '+12,5%',
            changeType: 'positive',
            color: 'primary'
          },
          {
            icon: 'Heart',
            label: 'Etkileşim Oranı',
            value: `%${metricsData?.engagementRate}`,
            change: '+2,1%',
            changeType: 'positive',
            color: 'secondary'
          },
          {
            icon: 'TrendingUp',
            label: 'Aktif Kampanyalar',
            value: metricsData?.activeCampaigns,
            change: '+3',
            changeType: 'positive',
            color: 'accent'
          },
          {
            icon: 'DollarSign',
            label: 'Toplam Gelir',
            value: metricsData?.totalRevenue,
            change: '+18,3%',
            changeType: 'positive',
            color: 'success'
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading metrics:', error);
    }
  };

  const getGreeting = () => {
    const hour = currentTime?.getHours();
    if (hour < 12) return 'Günaydın';
    if (hour < 18) return 'İyi günler';
    return 'İyi akşamlar';
  };

  const recentMessages = [
  {
    id: 1,
    sender: "Ayşe Müzik",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1400f5ad7-1763296600804.png",
    avatarAlt: "Professional Turkish woman with long dark hair wearing elegant black blazer, warm smile against neutral background",
    content: "Kampanya detaylarını inceledim, çok beğendim! Yarın görüşelim mi?",
    timestamp: new Date(Date.now() - 15 * 60000),
    unread: true,
    campaignId: 1
  },
  {
    id: 2,
    sender: "Mehmet Beats",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11cf57b7a-1763293855270.png",
    avatarAlt: "Young Turkish male DJ with short styled hair wearing casual denim jacket, confident expression in modern studio setting",
    content: "Story içeriğini hazırladım, onayınıza sunuyorum.",
    timestamp: new Date(Date.now() - 45 * 60000),
    unread: true,
    campaignId: 2
  },
  {
    id: 3,
    sender: "Zeynep Ses",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_162a6382f-1763296408905.png",
    avatarAlt: "Professional Turkish female vocal coach with shoulder-length brown hair wearing white blouse, friendly demeanor in bright studio",
    content: "Teşekkürler! Ödeme işlemi tamamlandı.",
    timestamp: new Date(Date.now() - 120 * 60000),
    unread: false,
    campaignId: 1
  },
  {
    id: 4,
    sender: "Can Ritim",
    avatar: "https://images.unsplash.com/photo-1562111607-51dfe40595c3",
    avatarAlt: "Young Turkish male music blogger with trendy haircut wearing casual hoodie, enthusiastic expression with headphones around neck",
    content: "Yeni teklif için müsait misiniz?",
    timestamp: new Date(Date.now() - 180 * 60000),
    unread: false,
    campaignId: 3
  }];


  const quickActions = [
  {
    title: "Yeni Kampanya Oluştur",
    description: "Müziğinizi tanıtmak için kampanya başlatın",
    icon: "Plus",
    color: "primary",
    path: "/campaign-management?action=create"
  },
  {
    title: "Influencer Keşfet",
    description: "Size uygun influencer\'ları bulun",
    icon: "Search",
    color: "secondary",
    path: "/influencer-discovery"
  },
  {
    title: "Teklifleri İncele",
    description: "Bekleyen teklifleri görüntüleyin",
    icon: "FileText",
    color: "accent",
    path: "/campaign-management?tab=proposals"
  },
  {
    title: "Ödeme Merkezi",
    description: "Ödemelerinizi yönetin",
    icon: "CreditCard",
    color: "success",
    path: "/payment-center"
  }];


  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 pb-20 md:pb-6">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {getGreeting()}, {user?.email?.split('@')?.[0] || 'Sanatçı'}!
          </h1>
          <p className="text-muted-foreground">
            {currentTime?.toLocaleDateString('tr-TR', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        )}

        {/* Dashboard Content */}
        {!loading && (
          <>
            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {metrics?.map((metric, index) => (
                <MetricsCard key={index} {...metric} />
              ))}
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Left Column - Campaigns */}
              <div className="lg:col-span-2 space-y-6">
                {/* Active Campaigns */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-foreground">Aktif Kampanyalar</h2>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate('/campaign-management')}
                    >
                      Tümünü Gör
                    </Button>
                  </div>
                  {campaigns?.length === 0 ? (
                    <div className="bg-card border border-border rounded-lg p-8 text-center">
                      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon name="Briefcase" size={32} className="text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">Henüz kampanya yok</h3>
                      <p className="text-muted-foreground mb-4">İlk kampanyanızı oluşturarak influencer'larla çalışmaya başlayın</p>
                      <Button 
                        variant="default"
                        onClick={() => navigate('/campaign-management?action=create')}
                      >
                        Kampanya Oluştur
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {campaigns?.slice(0, 4)?.map((campaign) => (
                        <CampaignCard key={campaign?.id} campaign={campaign} />
                      ))}
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-4">Hızlı İşlemler</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {quickActions?.map((action, index) => (
                      <QuickActionCard key={index} {...action} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column - Sidebar */}
              <div className="space-y-6">
                {/* Financial Summary */}
                {financialData && <FinancialSummary data={financialData} />}

                {/* Recent Messages */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">Son Mesajlar</h3>
                    <Button variant="ghost" size="sm">
                      Tümü
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {recentMessages?.map((message) => (
                      <RecentMessage key={message?.id} message={message} />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Recommended Influencers */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-foreground">Önerilen Influencer'lar</h2>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate('/influencer-discovery')}
                >
                  Tümünü Gör
                </Button>
              </div>
              {recommendedInfluencers?.length === 0 ? (
                <div className="bg-card border border-border rounded-lg p-8 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Users" size={32} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Henüz öneri yok</h3>
                  <p className="text-muted-foreground mb-4">Influencer keşfet sayfasından size uygun influencer'ları bulabilirsiniz</p>
                  <Button 
                    variant="default"
                    onClick={() => navigate('/influencer-discovery')}
                  >
                    Influencer Keşfet
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {recommendedInfluencers?.map((influencer) => (
                    <RecommendedInfluencer key={influencer?.id} influencer={influencer} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <MobileNav />
      <QuickActions />
    </div>
  );
};

export default ArtistDashboard;